import React from 'react';

import { RoundedStarFilled } from '@depop/web-thrift-ui/Icons/RoundedStarFilled';
import { RoundedStarHalf } from '@depop/web-thrift-ui/Icons/RoundedStarHalf';
import { useTranslations } from 'next-intl';

import { ButtonMinimal } from '../ButtonMinimal';

import styles from './styles.module.css';

type Props = {
  rating: number;
  size?: 'sm' | 'md' | 'lg';
  onStarClick?: (index: number) => unknown;
};

export function FeedbackStars({ rating, size = 'sm', onStarClick }: Props) {
  const stars = Array(5).fill(
    <span className={styles.emptyStar}>
      <RoundedStarFilled size={size} aria-hidden="true" />
    </span>
  );
  const remainder = rating % 1;
  const t = useTranslations('common');

  let fullStarsCount;

  if (remainder > 0.5) {
    fullStarsCount = Math.ceil(rating);
  } else {
    fullStarsCount = Math.floor(rating);
  }

  const shouldAddHalfStar = remainder <= 0.5 && remainder > 0;

  stars.fill(
    <span className={styles.filledStar}>
      <RoundedStarFilled size={size} aria-hidden="true" />
    </span>,
    0,
    fullStarsCount
  );

  if (fullStarsCount < 5 && shouldAddHalfStar) {
    stars.splice(
      fullStarsCount,
      1,
      <span className={styles.filledStar}>
        <RoundedStarHalf size={size} aria-hidden="true" />
      </span>
    );
  }

  function calculateDisplayedRating(
    rating: number,
    remainder: number,
    shouldAddHalfStar: boolean,
    fullStarsCount: number
  ): number {
    if (remainder > 0.5) {
      return Math.ceil(rating);
    }
    if (shouldAddHalfStar) {
      return fullStarsCount + 0.5;
    }
    return fullStarsCount;
  }

  const isDisplayMode = !onStarClick;
  const displayedRating = calculateDisplayedRating(
    rating,
    remainder,
    shouldAddHalfStar,
    fullStarsCount
  );

  const ariaLabel = isDisplayMode
    ? t('SellerFeedbackStarsAriaLabel', { rating: displayedRating })
    : t('SellerFeedbackStarsDisplayLabel', { rating: displayedRating });

  const starsContent = stars.map((star, index) => {
    return onStarClick ? (
      <ButtonMinimal
        onClick={() => onStarClick && onStarClick(index)}
        key={index}
        type="button"
        aria-label={`${index + 1} star${index + 1 !== 1 ? 's' : ''}`}
      >
        {star}
      </ButtonMinimal>
    ) : (
      <React.Fragment key={index}>{star}</React.Fragment>
    );
  });

  return (
    <span role={isDisplayMode ? 'img' : 'group'} aria-label={ariaLabel}>
      <span aria-hidden="true" className={styles.starsRow}>
        {starsContent}
      </span>
    </span>
  );
}
